import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { cartLabels, openTimings } from '@/data/content';
import Calendar from '@/components/ui/Calendar';
import AvailabilityStrip from '@/components/ui/AvailabilityStrip';
import ApplyButton from '@/components/ui/ApplyButton';
import Button from '@/components/ui/Button';

interface OpenTimingPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (date: Date) => void;
    applyLabel: 'Counter' | 'Available';
    onFetchCount?: (date: Date) => Promise<number> | number;
    availableIsoDates?: Set<string>;
    minDate?: Date;
}

const OpenTimingPopup: React.FC<OpenTimingPopupProps> = ({
    isOpen,
    onClose,
    onSelect,
    applyLabel,
    onFetchCount,
    availableIsoDates,
    minDate,
}) => {
    // ─── Active weekdays derived from openTimings ────────────────────────────
    const activeDays = useMemo(() => {
        const days = new Set<number>();
        openTimings.forEach((ot) => {
            if (ot.meal_type === 'lunch' || ot.meal_type === 'dinner') days.add(ot.day_of_week);
        });
        return days;
    }, []);

    // ─── Restore last picked date from localStorage ──────────────────────────
    const initialDate = useMemo(() => {
        const raw = localStorage.getItem('selectedServiceDate');
        return raw ? new Date(raw) : null;
    }, [isOpen]);

    // ─── Keep ref to latest onFetchCount to avoid stale closures ────────────
    const onFetchCountRef = useRef(onFetchCount);
    useEffect(() => {
        onFetchCountRef.current = onFetchCount;
    });

    // ─── Local state ─────────────────────────────────────────────────────────
    const [pickedDate, setPickedDate] = useState<Date | null>(null);
    const [fetching, setFetching] = useState(false);

    /**
     * itemCount  — used when applyLabel === 'Counter'
     * isAvailable — used when applyLabel === 'Available'
     * Both reset to null whenever a new date is picked.
     */
    const [itemCount, setItemCount] = useState<number | null>(null);

    useEffect(() => {
        setPickedDate(initialDate);
        setItemCount(null);
        if (initialDate && onFetchCountRef.current) {
            setFetching(true);
            Promise.resolve(onFetchCountRef.current(initialDate))
                .then((result) => setItemCount(result))
                .finally(() => setFetching(false));
        }
    }, [initialDate]);

    // ─── Date selection ───────────────────────────────────────────────────────
    const handleDateSelect = useCallback(
        async (date: Date) => {
            setPickedDate(date);
            setItemCount(null);

            if (!onFetchCount) return;

            setFetching(true);
            try {
                const result = await onFetchCount(date);
                setItemCount(result);
            } finally {
                setFetching(false);
            }
        },
        [onFetchCount, applyLabel]
    );

    // ─── Apply / confirm ──────────────────────────────────────────────────────
    const handleApply = useCallback(() => {
        if (pickedDate) {
            localStorage.setItem('selectedServiceDate', pickedDate.toISOString());
            onSelect(pickedDate);
        }
        onClose();
    }, [pickedDate, onSelect, onClose]);

    // ─── Day-level timings for the picked date ────────────────────────────────
    const dayTimings = useMemo(() => {
        if (!pickedDate) return null;
        return openTimings.filter((ot) => ot.day_of_week === pickedDate.getDay());
    }, [pickedDate]);

    const hasLunch = dayTimings?.some((t) => t.meal_type === 'lunch') ?? false;
    const hasDinner = dayTimings?.some((t) => t.meal_type === 'dinner') ?? false;
    const hasPickup = dayTimings?.some((t) => t.is_pick) ?? false;
    const hasDelivery = dayTimings?.some((t) => t.is_delivery) ?? false;

    // ─── Calendar date pool ───────────────────────────────────────────────────
    const mergedDates = useMemo(() => {
        const toIso = (d: Date) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        if (availableIsoDates && availableIsoDates.size > 0) {
            const filtered = new Set<string>();
            availableIsoDates.forEach((iso) => {
                const d = new Date(iso + 'T00:00:00');
                if (activeDays.has(d.getDay())) filtered.add(iso);
            });
            return filtered;
        }

        const dates = new Set<string>();
        const today = new Date();
        for (let i = 0; i < 60; i++) {
            const d = new Date(today);
            d.setDate(d.getDate() + i);
            if (activeDays.has(d.getDay())) dates.add(toIso(d));
        }
        return dates;
    }, [availableIsoDates, activeDays]);

    if (!isOpen) return null;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

            <div
                className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-5 max-w-xs w-full"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <Button
                    variant="ghost"
                    onClick={onClose}
                    className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center bg-app-primary text-white rounded-full shadow-md shadow-app-primary/20 hover:bg-app-primary/90 transition-colors text-lg leading-none"
                    aria-label={cartLabels.close_label}
                >
                    ×
                </Button>

                {/* Header */}
                <p className="text-sm font-medium text-app-text mb-3">{cartLabels.select_date_label}</p>

                {/* Calendar */}
                <Calendar
                    selected={pickedDate}
                    onSelect={handleDateSelect}
                    minDate={minDate ?? new Date()}
                    availableIsoDates={mergedDates}
                />

                {/* Availability icons */}
                <AvailabilityStrip
                    pickedDate={pickedDate}
                    hasLunch={hasLunch}
                    hasDinner={hasDinner}
                    hasPickup={hasPickup}
                    hasDelivery={hasDelivery}
                />

                {/* Action row */}
                <div className="flex gap-2 mt-4">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={onClose}
                        className="flex-1 !shadow-none hover:!translate-y-0"
                    >
                        {cartLabels.diff_slot_cancel}
                    </Button>

                    <ApplyButton
                        applyLabel={applyLabel}
                        pickedDate={pickedDate}
                        fetching={fetching}
                        itemCount={itemCount}
                        onApply={handleApply}
                    />
                </div>
            </div>
        </div>
    );
};

export default OpenTimingPopup;
